/**
 * Executions API (Orchestration)
 * Routes to orchestration-service via /v1/executions
 */

import { apiClient } from './api-client'

export interface ExecutionResponse {
  id: string
  mission_id: string
  status: string // 'pending' | 'running' | 'paused' | 'completed' | 'failed' | 'cancelled'
  progress: number
  created_at: string
  started_at?: string
  completed_at?: string
  /** The Edge host dispatch accepted this run on (agion/Agion#2485); absent when unknown. */
  edge_host_id?: string | null
  /**
   * The run's end record (P-095 slice 8, agion/Agion#2939), present only with
   * MISSION_RUN_RESULT_ENABLED on: the Run Result and the stored codes. Read
   * through utils/run-end.ts.
   */
  result?: string
  codes?: unknown
  /**
   * Whether the reader reads this run's content (agion/Agion#3019). The
   * server decides it: where six-level governance is active only the
   * person who started the run does. False means the run shows its
   * metadata and the closed-content line, no messages.
   */
  content_open?: boolean
  /** Whose content the run's organisation opens: `readers` or `starter`. */
  content_scope?: string
  /** Who started the run: `user`, `customer`, `trigger`, `system` or `escalation`. */
  initiator_type?: string
}

export interface ExecutionListResponse {
  executions: ExecutionResponse[]
  total: number
  limit: number
  offset: number
}

/**
 * ExecutionOutput — a versioned artifact produced by a mission execution.
 * Backend sends snake_case JSON tags; this interface matches directly (no camelCase conversion).
 */
export interface ExecutionOutput {
  id: string
  execution_id: string
  version_number: number
  content_type: string
  title: string
  content: Record<string, unknown>
  created_by: string | null
  created_at: string
  is_current: boolean
  restored_from_id: string | null
}

export interface ExecutionOutputsResponse {
  outputs: ExecutionOutput[]
  total: number
}

/**
 * The run list's server-side scope (persona finding F-ACC-03): the reader's
 * own runs and runs of missions they were invited to. The server enforces it;
 * without a scope the list reads as before.
 */
export type ExecutionListScope = 'own_and_invited'

export const executionsApi = {
  /**
   * List recent executions across all missions
   */
  listExecutions: (limit: number = 20, offset: number = 0, scope?: ExecutionListScope) => {
    return apiClient.get<ExecutionListResponse>('/v1/executions', {
      limit: limit.toString(),
      offset: offset.toString(),
      ...(scope ? { scope } : {}),
    })
  },

  /**
   * List one mission's executions, newest first, a page at a time
   * (agion/Agion#2874). The server caps `limit` at 100.
   */
  listMissionExecutions: (missionId: string, limit: number = 20, offset: number = 0, scope?: ExecutionListScope) => {
    return apiClient.get<ExecutionListResponse>('/v1/executions', {
      mission_id: missionId,
      limit: limit.toString(),
      offset: offset.toString(),
      ...(scope ? { scope } : {}),
    })
  },

  /**
   * Get a single execution by ID
   */
  getExecution: (executionId: string) => {
    return apiClient.get<ExecutionResponse>(`/v1/executions/${executionId}`)
  },

  /**
   * List all versioned outputs for an execution (ART-04)
   * Returns all output versions ordered by version_number descending
   */
  getOutputs: (executionId: string) => {
    return apiClient.get<ExecutionOutputsResponse>(`/v1/executions/${executionId}/outputs`)
  },

  /**
   * Restore a previous output version (ART-04)
   * Creates a new output record as current, copied from the specified outputId
   */
  restoreOutput: (executionId: string, outputId: string) => {
    return apiClient.post<ExecutionOutput>(`/v1/executions/${executionId}/outputs/${outputId}/restore`)
  },
}
